// Credit-pack presentation helpers shared by /pricing, the home page, the
// converter page and the AI generator's pricing block.
//
// GET /api/products/credits returns only PUBLIC packs, so the count varies
// (3 or 4 today). Nothing here may assume a fixed number of packs or pick a
// pack by position: names come from the pack size, and the "best value"
// badge goes to whichever pack is actually cheapest per credit.

// Names for the known pack sizes. The first three are the long-standing
// titles; 500 is the large pack added when the unlimited plan was retired.
const PACK_TITLES_BY_CREDITS = {
  5: 'Starter',
  25: 'Basic',
  100: 'Business',
  500: 'Studio',
};

// Fallback for a pack size that has no name above: order in the list.
const PACK_TITLES_BY_POSITION = ['Starter', 'Basic', 'Business', 'Studio', 'Team'];

const pricePerCredit = (pack) => {
  const price = Number(pack?.price);
  const credits = Number(pack?.credits);
  if (!Number.isFinite(price) || !Number.isFinite(credits) || credits <= 0) return null;
  return price / credits;
};

/** Packs sorted by size (smallest first); invalid rows are dropped. */
export const sortPacks = (packs) => (packs || [])
  .filter((pack) => pack && Number(pack.credits) > 0)
  .slice()
  .sort((a, b) => Number(a.credits) - Number(b.credits));

/** Display name for a pack, e.g. "Starter". `index` is its position in the sorted list. */
export const packTitle = (pack, index) => PACK_TITLES_BY_CREDITS[Number(pack?.credits)]
  || PACK_TITLES_BY_POSITION[index]
  || `${pack?.credits} credits`;

/** "€0.500/credit"-style string, or '' when the price is unusable. */
export const formatPricePerCredit = (pack, separator = '/') => {
  const value = pricePerCredit(pack);
  return value === null ? '' : `€${value.toFixed(3)}${separator}credit`;
};

/**
 * Index of the pack with the lowest €/credit, or -1 when there is nothing to
 * compare (fewer than two priced packs — a single pack is not a "best" deal).
 */
export const bestValueIndex = (packs) => {
  let best = -1;
  let bestValue = Infinity;
  let priced = 0;
  (packs || []).forEach((pack, i) => {
    const value = pricePerCredit(pack);
    if (value === null) return;
    priced += 1;
    if (value < bestValue) {
      bestValue = value;
      best = i;
    }
  });
  return priced > 1 ? best : -1;
};
